import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../../../i18n';
import { dateLocale } from '../../../lib/localePrefs';
import type {
  DrillPanel,
  NorthStarPayload,
  NorthStarPlanItem,
  NorthStarWindow,
  PerfSettings,
  SettingsIssueGroup,
} from '../types';
import { Icon } from './shared/Icon';
import { DrillToolbar, DrillToolbarStat } from './shared/DrillToolbar';
import { ImprovedDriversPanel } from './drill/DrillPanels';
import { SEV_META } from '../data/insights';
import {
  formatComponentValue,
  formatNorthStarValue,
  formatNorthStarValueInline,
  navTargetLabel,
  planItemNavTarget,
  type NorthStarNavTarget,
} from '../lib/northStarFormat';
import {
  northStarSampleLabel,
  northStarWindowDates,
  northStarWindowLabel,
} from '../lib/northStarWindow';
import { effectiveCompareKind, formatRangeLabel } from '../lib/perfSettings';

const CATEGORY_META: Record<
  NorthStarPlanItem['category'],
  { get label(): string; icon: string }
> = {
  metric: { get label() { return i18n.t('performance:nsDrill.metric'); }, icon: 'trendUp' },
  settings: { get label() { return i18n.t('performance:nsDrill.catSettings'); }, icon: 'sliders' },
  operations: { get label() { return i18n.t('performance:nsDrill.catOps'); }, icon: 'activity' },
};

function CompositeTrend({
  series,
  bucketLabels,
  unitSuffix,
}: {
  series: number[];
  bucketLabels?: string[];
  unitSuffix: string;
}) {
  const { t } = useTranslation('performance');
  if (series.length < 2) {
    return null;
  }
  const max = Math.max(...series, 1);
  const min = Math.min(...series, 0);
  const span = Math.max(max - min, 1);
  return (
    <div className="drill-panel ns-drill-trend">
      <div className="dp-head">
        <h3>{t('nsDrill.overTime')}</h3>
      </div>
      <div className="ns-drill-bars">
        {series.map((v, i) => {
          const h = Math.max(8, ((v - min) / span) * 100);
          return (
            <div className="ns-drill-bar-col" key={i}>
              <span className="ns-drill-bar-val">
                {v}
                {unitSuffix === '%' ? '%' : ''}
              </span>
              <span className="ns-drill-bar-track">
                <span
                  className="ns-drill-bar-fill"
                  style={{ height: `${h}%` }}
                />
              </span>
              {bucketLabels?.[i] && (
                <span className="ns-drill-bar-cap">{bucketLabels[i]}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ComponentCards({
  payload,
  compareOn,
  onNavigate,
}: {
  payload: NorthStarPayload;
  /** False when the window has no comparison: no deltas then. */
  compareOn: boolean;
  onNavigate: (t: NorthStarNavTarget) => void;
}) {
  const { t } = useTranslation('performance');
  return (
    <div className="drill-panel">
      <div className="dp-head">
        <h3>{t('nsDrill.components')}</h3>
      </div>
      <div className="ns-comp-grid">
        {payload.componentBreakdown.map(c => {
          const delta =
            c.prior !== 0
              ? +(((c.value - c.prior) / Math.abs(c.prior)) * 100).toFixed(1)
              : 0;
          const target = planItemNavTarget({
            category: 'metric',
            metricKey: c.metricKey,
          });
          const clickable = target.kind !== 'overview';
          const sample = northStarSampleLabel(c.n, c.nBasis);
          return (
            <button
              key={c.metricKey}
              type="button"
              className={`ns-comp-card${clickable ? ' clickable' : ''}`}
              onClick={() => clickable && onNavigate(target)}
            >
              <div className="ns-comp-card-top">
                <span className="ns-comp-card-label">{c.label}</span>
                <span className="ns-comp-card-weight">
                  {t('nsDrill.weight', { weight: c.weight })}
                </span>
              </div>
              <div className="ns-comp-card-val">
                {formatComponentValue(c.value, c.unit)}
              </div>
              {compareOn && c.prior !== 0 && (
                <div
                  className={`ns-comp-card-delta${delta >= 0 ? ' up' : ' down'}`}
                >
                  <Icon name={delta >= 0 ? 'arrowUp' : 'arrowDown'} size={11} />
                  {t('nsDrill.vsCompare', { pct: Math.abs(delta) })}
                </div>
              )}
              {sample && <div className="ns-comp-card-n">{sample}</div>}
              {clickable && (
                <span className="ns-comp-card-go">
                  <Icon name="chevron" size={14} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SettingsPanel({
  issues,
  onNavigate,
}: {
  issues: SettingsIssueGroup[];
  onNavigate: (t: NorthStarNavTarget) => void;
}) {
  const { t } = useTranslation('performance');
  return (
    <div className="drill-panel">
      <div className="dp-head">
        <h3>{t('nsDrill.settings')}</h3>
        <button
          type="button"
          className="ns-plan-go"
          onClick={() => onNavigate({ kind: 'settings' })}
        >
          {t('nsDrill.openSettings')}
          <Icon name="chevron" size={13} />
        </button>
      </div>
      <div className="sm-issues">
        {issues.map(g => {
          const sev = SEV_META[g.severity] ?? SEV_META.info;
          return (
            <div key={g.id} className="ins-card sm-issue">
              <div className="ins-head">
                <span className="ins-dot" style={{ background: sev.c }} />
                <strong>{g.title}</strong>
              </div>
              <div className="sm-issue-impact">{g.impact}</div>
              <div className="ins-action">
                <Icon name="check" size={12} />
                <span>{g.recommendation}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActionPlan({
  items,
  generatedAt,
  onNavigate,
}: {
  items: NorthStarPlanItem[];
  generatedAt?: string;
  onNavigate: (t: NorthStarNavTarget) => void;
}) {
  const { t } = useTranslation('performance');
  if (!items.length) {
    return null;
  }
  return (
    <div className="drill-panel ns-plan-panel">
      <div className="dp-head">
        <h3>{t('nsDrill.plan')}</h3>
        {generatedAt && (
          <span className="ns-plan-stamp">
            {t('nsDrill.generated', {
              date: new Date(generatedAt).toLocaleDateString(
                dateLocale(currentLocale()),
              ),
            })}
          </span>
        )}
      </div>
      <ol className="ns-plan-list">
        {items.map(item => {
          const cat = CATEGORY_META[item.category];
          const target = planItemNavTarget(item);
          return (
            <li key={item.priority} className="ns-plan-item">
              <div className="ns-plan-item-head">
                <span className="ns-plan-priority">{item.priority}</span>
                <strong className="ns-plan-title">{item.title}</strong>
                <span className={`ns-plan-cat ns-plan-cat-${item.category}`}>
                  <Icon name={cat.icon} size={11} />
                  {cat.label}
                </span>
              </div>
              {item.why && <p className="ns-plan-why">{item.why}</p>}
              {item.action && <p className="ns-plan-action">{item.action}</p>}
              <button
                type="button"
                className="ns-plan-go"
                onClick={() => onNavigate(target)}
              >
                {navTargetLabel(target)}
                <Icon name="chevron" size={13} />
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The saved baseline's dates in the person's language ("1 – 28 Jan 2026"). */
function baselineRange(w: NorthStarPayload['baseline']['window']): string {
  return ISO_DAY.test(w.start) && ISO_DAY.test(w.end)
    ? formatRangeLabel(w.start, w.end)
    : `${w.start} – ${w.end}`;
}

export function NorthStarDrillDown({
  payload,
  panels,
  settings,
  window,
  scopeLabel,
  bucketLabels,
  onClose,
  onNavigate,
}: {
  payload: NorthStarPayload;
  panels: DrillPanel[];
  /** The Performance view's frame settings: the drill's one window. */
  settings: PerfSettings;
  /** The window the server computed every number over (dates). */
  window?: NorthStarWindow | null;
  scopeLabel?: string;
  bucketLabels?: string[];
  onClose: () => void;
  onNavigate: (t: NorthStarNavTarget) => void;
}) {
  const { t } = useTranslation('performance');
  const { value, suffix } = formatNorthStarValue(
    payload.currentValue,
    payload.spec.unit,
  );
  const baselineDelta =
    payload.baseline.value !== 0
      ? +(
          ((payload.currentValue - payload.baseline.value) /
            Math.abs(payload.baseline.value)) *
          100
        ).toFixed(1)
      : payload.trendPct;
  const improving = baselineDelta >= 0;
  const windowLabel = northStarWindowLabel(settings, window);
  const compareOn = window ? !!window.compare : effectiveCompareKind(settings) !== 'off';

  const narrativePanels = panels.filter(
    (p): p is Extract<DrillPanel, { kind: 'narrative' }> =>
      p.kind === 'narrative',
  );
  const driversPanels = panels.filter(
    (p): p is Extract<DrillPanel, { kind: 'drivers' }> => p.kind === 'drivers',
  );
  const settingsPanel = panels.find(
    (p): p is Extract<DrillPanel, { kind: 'settingsRecommendations' }> =>
      p.kind === 'settingsRecommendations',
  );
  const planPanel = panels.find(
    (p): p is Extract<DrillPanel, { kind: 'plan' }> => p.kind === 'plan',
  );

  return (
    <div className="drill drill-below-topbar ns-drill" data-ftue="north-star">
      <DrillToolbar
        onClose={onClose}
        hero={
          <DrillToolbarStat
            label={payload.label}
            value={
              <>
                {value}
                {suffix && <span className="ns-drill-hero-unit">{suffix}</span>}
              </>
            }
            trend={
              <div
                className="drill-toolbar-trend"
                style={{
                  color: improving ? 'var(--st-steady)' : 'var(--st-alert)',
                }}
              >
                <Icon
                  name={improving ? 'arrowUp' : 'arrowDown'}
                  size={13}
                  stroke={2.4}
                />
                {t('northStar.vsBaseline', { pct: Math.abs(baselineDelta) })}
              </div>
            }
          />
        }
      />

      <div className="drill-body">
        <div className="ns-drill-intro drill-panel">
          <p
            className="ns-drill-window"
            title={northStarWindowDates(window)}
          >
            {windowLabel}
          </p>
          {payload.description && (
            <p className="ns-drill-desc">{payload.description}</p>
          )}
          <div className="ns-drill-baseline">
            <span>
              {t('nsDrill.baselineSet', {
                value: formatNorthStarValueInline(
                  payload.baseline.value,
                  payload.spec.unit,
                ),
                range: baselineRange(payload.baseline.window),
              })}
            </span>
            {scopeLabel && (
              <>
                <span className="ns-drill-baseline-sep">·</span>
                <span>{scopeLabel}</span>
              </>
            )}
          </div>
        </div>

        <CompositeTrend
          series={payload.series}
          bucketLabels={bucketLabels}
          unitSuffix={suffix}
        />

        <ComponentCards
          payload={payload}
          compareOn={compareOn}
          onNavigate={onNavigate}
        />

        {narrativePanels.map((p, i) => {
          const meta = SEV_META[p.sev] ?? SEV_META.info;
          return (
            <div className="drill-panel ns-drill-weak" key={`narr${i}`}>
              <div className="ins-head">
                <span className="ins-dot" style={{ background: meta.c }} />
                <strong>{p.title}</strong>
              </div>
              <p className="ns-drill-weak-why">{p.why}</p>
              {p.action && <p className="ns-drill-weak-action">{p.action}</p>}
            </div>
          );
        })}

        {driversPanels.map((dp, i) => (
          <ImprovedDriversPanel
            key={`drv${i}`}
            title={dp.title}
            dimensions={dp.dimensions}
            fmtVal={(v: number) => String(v)}
          />
        ))}

        {settingsPanel && settingsPanel.issues.length > 0 && (
          <SettingsPanel issues={settingsPanel.issues} onNavigate={onNavigate} />
        )}

        {planPanel && (
          <ActionPlan
            items={planPanel.items}
            generatedAt={planPanel.generatedAt}
            onNavigate={onNavigate}
          />
        )}
      </div>
    </div>
  );
}
